// frontend/src/pages/CryptoWithdraw.jsx
// USDT crypto withdrawal — move internal USDT to external wallets.
import { useState, useEffect, useCallback, useRef } from 'react';
import { cryptoWithdrawAPI, walletAPI } from '../services/api';
import { Card, Button, Badge, Spinner, Empty, Table, Td, SectionHeader, Modal } from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { Send, RefreshCw, ExternalLink, Copy, CheckCircle, XCircle, AlertCircle, Clock, Shield } from 'lucide-react';
import { format } from 'date-fns';

// ── Constants ─────────────────────────────────────────────────────────────────

const NETWORKS = [
  { id: 'BEP20', label: 'BEP-20',  chain: 'BNB Smart Chain', icon: '🟡', fee: 0.5,  explorer: 'https://bscscan.com/tx/'              },
  { id: 'TRC20', label: 'TRC-20',  chain: 'TRON Network',    icon: '🔴', fee: 0.5,  explorer: 'https://tronscan.org/#/transaction/'  },
  { id: 'ERC20', label: 'ERC-20',  chain: 'Ethereum',        icon: '🔵', fee: 5.0,  explorer: 'https://etherscan.io/tx/'             },
];

const STATUS_COLOR = {
  pending:   'amber',
  approved:  'cyan',
  sent:      'purple',
  completed: 'green',
  rejected:  'red',
  cancelled: 'gray',
};

const STATUS_ICON = {
  pending:   '⏳',
  approved:  '✅',
  sent:      '🚀',
  completed: '✔️',
  rejected:  '❌',
  cancelled: '🚫',
};

function shortAddr(addr) {
  if (!addr || addr.length <= 14) return addr;
  return addr.slice(0, 6) + '…' + addr.slice(-4);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CryptoWithdraw() {
  const [tab, setTab] = useState('withdraw');

  // State
  const [fees, setFees]               = useState(null);
  const [usdtBalance, setUsdtBalance] = useState(0);
  const [network, setNetwork]         = useState('BEP20');
  const [amount, setAmount]           = useState('');
  const [wallet, setWallet]           = useState('');
  const [quote, setQuote]             = useState(null);
  const [quoting, setQuoting]         = useState(false);
  const [addrError, setAddrError]     = useState('');
  const [confirmed, setConfirmed]     = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [history, setHistory]         = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Confirmation screen state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const quoteTimer = useRef(null);

  // ── Load data ───────────────────────────────────────────────────────────────

  const loadFees = useCallback(async () => {
    try {
      const [feesRes, walletRes] = await Promise.allSettled([
        cryptoWithdrawAPI.getFees(),
        walletAPI.getBalance(),
      ]);
      if (feesRes.status === 'fulfilled')   setFees(feesRes.value.fees);
      if (walletRes.status === 'fulfilled') setUsdtBalance(walletRes.value.wallet?.usdtBalance || 0);
    } catch { /* silent */ }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await cryptoWithdrawAPI.getHistory({ limit: 30 });
      setHistory(res.withdrawals || []);
    } catch { setHistory([]); }
    finally { setHistLoading(false); }
  }, []);

  useEffect(() => { loadFees(); }, [loadFees]);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  // ── Auto-quote on input change ──────────────────────────────────────────────

  useEffect(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setQuote(null); return; }
    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(async () => {
      setQuoting(true);
      try {
        const res = await cryptoWithdrawAPI.getQuote(amt, network);
        setQuote(res.quote);
      } catch (err) {
        setQuote(null);
        if (err.message) toast.error(err.message, { id: 'quote-err' });
      } finally {
        setQuoting(false);
      }
    }, 500);
    return () => clearTimeout(quoteTimer.current);
  }, [amount, network]);

  // ── Wallet address validation ───────────────────────────────────────────────

  const validateAddr = useCallback((addr, net) => {
    if (!addr) { setAddrError(''); return false; }
    if (net === 'TRC20') {
      if (!/^T[A-Za-z0-9]{33}$/.test(addr)) {
        setAddrError('TRC-20 address must start with "T" and be exactly 34 characters');
        return false;
      }
    } else {
      if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) {
        setAddrError(`${net} address must start with "0x" and be exactly 42 characters`);
        return false;
      }
    }
    setAddrError('');
    return true;
  }, []);

  useEffect(() => { if (wallet) validateAddr(wallet, network); }, [wallet, network, validateAddr]);

  // ── Open confirmation screen ────────────────────────────────────────────────

  const handleReview = () => {
    if (!quote)                         return toast.error('Please wait for the quote to load');
    if (!wallet)                        return toast.error('Enter your destination wallet address');
    if (!validateAddr(wallet, network)) return;
    setConfirmModalOpen(true);
  };

  // ── Final submit (called from confirmation modal) ───────────────────────────

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await cryptoWithdrawAPI.request({ amount: parseFloat(amount), destinationWallet: wallet, network });
      toast.success('Withdrawal request submitted!');
      setConfirmModalOpen(false);
      setAmount('');
      setWallet('');
      setQuote(null);
      setConfirmed(false);
      loadFees();
      setTab('history');
      loadHistory();
    } catch (err) {
      setConfirmModalOpen(false);
      toast.error(err.message || 'Failed to submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Copied!', { duration: 1500 }));

  const networkFee = fees?.networks?.[network]?.networkFee ?? NETWORKS.find(n => n.id === network)?.fee ?? 0;

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">Withdraw USDT</h1>
          <p className="text-text-secondary text-sm mt-1">Send your internal USDT to Binance, Trust Wallet, OKX, Bybit, MetaMask, or any compatible wallet.</p>
        </div>
        <button onClick={loadFees} className="text-text-muted hover:text-cyan p-2 rounded-xl hover:bg-cyan/10 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* USDT Balance banner */}
      <div className="bg-gradient-to-r from-cyan/10 via-bg-panel to-bg-panel border border-cyan/20 rounded-2xl p-5 flex flex-wrap gap-6 items-center">
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold mb-0.5">Internal USDT Balance</p>
          <p className="text-3xl font-black text-cyan">₮ {usdtBalance.toFixed(4)}</p>
          <p className="text-text-muted text-xs mt-1">Available to withdraw</p>
        </div>
        <div className="ml-auto flex flex-col gap-2 text-right">
          <Badge label={fees?.enabled !== false ? 'Withdrawals Open' : 'Withdrawals Paused'} color={fees?.enabled !== false ? 'green' : 'red'} />
          <p className="text-text-muted text-[10px]">Min: {fees?.minUsdt || 5} USDT · Max: {(fees?.maxUsdt || 10000).toLocaleString()} USDT</p>
        </div>
      </div>

      {/* Notice about internal vs on-chain */}
      <div className="flex items-start gap-3 bg-amber/5 border border-amber/20 rounded-xl p-3">
        <AlertCircle className="w-4 h-4 text-amber flex-shrink-0 mt-0.5" />
        <p className="text-amber text-xs leading-relaxed">
          <strong>Internal USDT</strong> is held in your DWI Fintech account. When you withdraw, USDT is sent from our treasury wallet to your external wallet on the blockchain. Network fees apply.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[['withdraw', '₮ Withdraw'], ['history', 'History']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-2 rounded-xl text-sm font-semibold transition-all',
              tab === id ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            )}
          >{label}</button>
        ))}
      </div>

      {/* ── Withdraw Form ── */}
      {tab === 'withdraw' && (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <Card>
              <SectionHeader title="Withdrawal Details" />
              <div className="space-y-5">

                {/* Network selector */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-text-muted uppercase tracking-wider">Blockchain Network</label>
                  <div className="grid grid-cols-3 gap-2">
                    {NETWORKS.map(n => (
                      <button
                        key={n.id}
                        onClick={() => { setNetwork(n.id); if (wallet) validateAddr(wallet, n.id); }}
                        className={clsx(
                          'flex flex-col items-center gap-1 py-3 px-2 rounded-xl border text-xs font-semibold transition-all',
                          network === n.id
                            ? 'border-cyan bg-cyan/10 text-cyan'
                            : 'border-border bg-bg-panel text-text-secondary hover:border-border-hi'
                        )}
                      >
                        <span className="text-xl">{n.icon}</span>
                        <span>{n.label}</span>
                        <span className="text-[10px] text-text-muted font-normal">Fee: {n.fee} USDT</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Amount */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-text-muted uppercase tracking-wider">Amount (USDT)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan font-black text-sm">₮</span>
                    <input
                      type="number"
                      className="w-full bg-bg-panel border border-border rounded-xl pl-9 pr-20 py-3.5 text-text-primary text-lg font-bold outline-none focus:border-cyan focus:ring-1 focus:ring-cyan/30 placeholder:text-text-muted"
                      placeholder="0.00"
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      min="0"
                      step="0.01"
                    />
                    <button
                      onClick={() => setAmount(Math.max(0, usdtBalance).toFixed(4))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-cyan bg-cyan/10 px-2 py-1 rounded-lg hover:bg-cyan/20 transition-all"
                    >
                      MAX
                    </button>
                  </div>
                  <p className="text-xs text-text-muted">
                    Balance: <span className="text-cyan font-semibold">₮ {usdtBalance.toFixed(4)}</span>
                  </p>
                </div>

                {/* Quote */}
                <div className={clsx(
                  'rounded-xl border transition-all',
                  quoting ? 'bg-bg-panel border-border' : quote ? 'bg-cyan/5 border-cyan/30' : 'bg-bg-panel border-border'
                )}>
                  {quoting ? (
                    <div className="flex items-center gap-2 p-4 text-text-muted text-sm">
                      <Spinner size="sm" /> Calculating…
                    </div>
                  ) : quote ? (
                    <div className="p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Gross amount</span>
                        <span className="font-semibold">{quote.amount} USDT</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Network fee ({network})</span>
                        <span className="text-amber font-semibold">− {quote.networkFee} USDT</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Platform fee</span>
                        <span className="text-amber font-semibold">− {quote.platformFee} USDT</span>
                      </div>
                      <div className="border-t border-border/60 my-1" />
                      <div className="flex justify-between">
                        <span className="text-text-primary font-bold">You receive</span>
                        <span className="text-green font-black text-xl">₮ {quote.netAmount}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-4 text-text-muted text-sm">
                      <AlertCircle className="w-4 h-4" />
                      Enter an amount to see the fee breakdown
                    </div>
                  )}
                </div>

                {/* Destination wallet */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-text-muted uppercase tracking-wider">
                    Destination Wallet Address ({network})
                  </label>
                  <input
                    type="text"
                    className={clsx(
                      'w-full bg-bg-panel border rounded-xl px-4 py-3 text-text-primary text-sm font-mono outline-none focus:ring-1 transition-all placeholder:text-text-muted',
                      addrError ? 'border-red focus:border-red focus:ring-red/30' : 'border-border focus:border-cyan focus:ring-cyan/30'
                    )}
                    placeholder={network === 'TRC20' ? 'TXxx... (34 chars)' : '0x... (42 chars)'}
                    value={wallet}
                    onChange={e => setWallet(e.target.value)}
                    spellCheck={false}
                  />
                  {addrError ? (
                    <p className="text-red text-xs flex items-center gap-1"><XCircle className="w-3 h-3" /> {addrError}</p>
                  ) : wallet && !addrError ? (
                    <p className="text-green text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Valid {network} address</p>
                  ) : (
                    <p className="text-text-muted text-xs">{network === 'TRC20' ? 'Starts with T, 34 characters' : 'Starts with 0x, 42 characters'}</p>
                  )}
                </div>

                {/* Review & Submit */}
                <Button
                  className="w-full"
                  disabled={!quote || quoting || !!addrError || !wallet}
                  onClick={handleReview}
                >
                  <Send className="w-4 h-4" />
                  Review Withdrawal →
                </Button>
              </div>
            </Card>
          </div>

          {/* Right: Info panel */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <SectionHeader title="How Withdrawals Work" />
              <div className="space-y-4">
                {[
                  { n: 1, t: 'Enter amount',       d: 'Type how much USDT to withdraw. Fees are deducted from the gross amount.' },
                  { n: 2, t: 'Choose network',      d: 'BEP-20 and TRC-20 have lower fees. ERC-20 (Ethereum) has higher gas costs.' },
                  { n: 3, t: 'Enter destination',   d: 'Paste your Binance, Trust Wallet, OKX, Bybit, or MetaMask address.' },
                  { n: 4, t: 'Confirm & submit',    d: 'USDT is locked from your internal balance. Admin reviews and sends within 24h.' },
                  { n: 5, t: 'Track on blockchain', d: 'You will receive a blockchain TX ID to verify your withdrawal on BSCScan / Tronscan / Etherscan.' },
                ].map(({ n, t, d }) => (
                  <div key={n} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-cyan flex items-center justify-center text-bg font-black text-xs flex-shrink-0 mt-0.5">{n}</div>
                    <div>
                      <p className="text-text-primary text-xs font-bold">{t}</p>
                      <p className="text-text-muted text-xs mt-0.5 leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <SectionHeader title="Network Fees" />
              <div className="space-y-3">
                {NETWORKS.map(n => {
                  const liveFee = fees?.networks?.[n.id]?.networkFee ?? n.fee;
                  const platFee = fees?.platformFee ?? 0.5;
                  return (
                    <div key={n.id} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
                      <div className="flex items-center gap-2">
                        <span>{n.icon}</span>
                        <div>
                          <p className="text-xs font-bold text-text-primary">{n.label}</p>
                          <p className="text-[10px] text-text-muted">{n.chain}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-amber">{liveFee} USDT</p>
                        <p className="text-[10px] text-text-muted">+ {platFee} platform</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card>
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 text-cyan flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-text-primary mb-1">Security Notice</p>
                  <p className="text-text-muted text-[11px] leading-relaxed">
                    DWI Fintech will never ask you to share your wallet's private key or seed phrase.
                    Always double-check the destination address — crypto is irreversible.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── Confirmation Modal ── */}
      <Modal
        open={confirmModalOpen}
        onClose={() => !submitting && setConfirmModalOpen(false)}
        title="Confirm Withdrawal"
        size="sm"
      >
        {quote && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-bg-elevated border border-border rounded-xl divide-y divide-border/40">
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-text-muted">Network</span>
                <span className="font-bold">
                  {NETWORKS.find(n => n.id === network)?.icon} {network} · {NETWORKS.find(n => n.id === network)?.chain}
                </span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-text-muted">Gross amount</span>
                <span className="font-semibold">₮ {quote.amount}</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-text-muted">Network fee</span>
                <span className="text-amber font-semibold">− ₮ {quote.networkFee}</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-text-muted">Platform fee</span>
                <span className="text-amber font-semibold">− ₮ {quote.platformFee}</span>
              </div>
              <div className="px-4 py-3 flex justify-between">
                <span className="text-text-primary font-bold">You receive</span>
                <span className="text-green font-black text-xl">₮ {quote.netAmount}</span>
              </div>
            </div>

            {/* Destination */}
            <div className="bg-bg-panel border border-border rounded-xl p-3 space-y-1">
              <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold">Sending To</p>
              <p className="text-text-primary text-xs font-mono break-all">{wallet}</p>
              <p className="text-text-muted text-[10px]">{network} network · USDT</p>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 bg-red/5 border border-red/20 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 text-red flex-shrink-0 mt-0.5" />
              <p className="text-red text-xs leading-relaxed">
                <strong>Irreversible.</strong> Double-check the address above. Crypto withdrawals cannot be cancelled once submitted. Ensure this address accepts USDT on the {network} network.
              </p>
            </div>

            {/* Confirmation checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                className="accent-cyan w-4 h-4 mt-0.5"
              />
              <span className="text-xs text-text-secondary leading-relaxed">
                I confirm the address <span className="text-text-primary font-mono font-bold">{shortAddr(wallet)}</span> is correct and I accept the fee deduction of ₮ {quote.totalFees} USDT.
              </span>
            </label>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                loading={submitting}
                disabled={!confirmed || submitting}
                onClick={handleSubmit}
              >
                <Send className="w-4 h-4" />
                Confirm & Submit
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── History ── */}
      {tab === 'history' && (
        <Card>
          <SectionHeader
            title="Withdrawal History"
            action={
              <button onClick={loadHistory} className="text-cyan text-xs hover:underline flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            }
          />
          {histLoading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : history.length === 0 ? (
            <Empty
              icon={<Clock className="w-12 h-12 text-text-muted" />}
              title="No withdrawals yet"
              description="Your USDT withdrawal history will appear here."
            />
          ) : (
            <Table headers={['Date', 'Amount', 'Net USDT', 'Network', 'Wallet', 'Status', 'TX / Explorer']}>
              {history.map(w => (
                <tr key={w._id}>
                  <Td className="text-xs text-text-muted whitespace-nowrap">
                    {format(new Date(w.createdAt), 'dd MMM yy HH:mm')}
                  </Td>
                  <Td className="font-semibold text-sm">₮ {w.amount}</Td>
                  <Td className="font-black text-green text-sm">
                    ₮ {w.netAmount}
                    <span className="text-text-muted text-[10px] block">
                      fees: {(w.networkFee + w.platformFee).toFixed(2)} USDT
                    </span>
                  </Td>
                  <Td>
                    <span className="text-xs font-bold">
                      {NETWORKS.find(n => n.id === w.network)?.icon} {w.network}
                    </span>
                  </Td>
                  <Td>
                    <button onClick={() => copy(w.destinationWallet)}
                      className="text-xs font-mono text-text-secondary hover:text-cyan flex items-center gap-1">
                      {shortAddr(w.destinationWallet)}
                      <Copy className="w-3 h-3" />
                    </button>
                  </Td>
                  <Td>
                    <Badge label={STATUS_ICON[w.status] + ' ' + w.status} color={STATUS_COLOR[w.status]} />
                    {w.status === 'rejected' && w.adminNote && (
                      <p className="text-red text-[10px] mt-1">{w.adminNote}</p>
                    )}
                  </Td>
                  <Td>
                    {w.txHash ? (
                      <div className="flex flex-col gap-1">
                        <button onClick={() => copy(w.txHash)}
                          className="text-xs font-mono text-cyan hover:underline flex items-center gap-1">
                          {w.txHash.slice(0, 8)}…
                          <Copy className="w-3 h-3" />
                        </button>
                        {w.explorerUrl && (
                          <a href={w.explorerUrl} target="_blank" rel="noreferrer"
                            className="text-[10px] text-purple hover:underline flex items-center gap-1">
                            View on chain <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="text-text-muted text-xs">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
